import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  parseFolio,
  buildFolio,
  isFolioDigitValid,
  ParsedFolio,
  FOLIO_DIGIT_MAX_LENGTH,
} from '@modules/private/lease/data/utils/folio.util';

/**
 * Diálogo para editar el PRIMER número del folio (el `oldId`).
 *
 * Solo se permite editar el primer grupo numérico que va después de `R-`
 * (regla del owner: ese número ES el `oldId`).
 *
 * ⚠️ REGLA DEL OWNER (no la rompas): `oldId` = EXCLUSIVAMENTE ese número (1–999),
 * NO el folio anterior. Históricamente los ids de arrendamiento eran números 1–999;
 * por eso se llama `oldId` y se agrega al folio como el primer grupo tras `R-`
 * (`R-{oldId}-…`). Al guardar se persiste `oldId = dígito` (queda en sync con el folio).
 * (La app móvil mobile-rendafin M13 Iter C.3 guardaba el folio anterior aquí — mismo bug,
 * pendiente de alinear si se toca ese repo.)
 */
@Component({
  selector: 'app-edit-folio-modal',
  templateUrl: './edit-folio-modal.component.html',
  styleUrls: ['./edit-folio-modal.component.scss'],
})
export class EditFolioModalComponent implements OnChanges {
  /** Visibilidad del modal (one-way desde el padre). */
  @Input() visible = false;
  /** Folio completo actual del lease (`data?.folio`). */
  @Input() folio: string | null | undefined = null;
  /** Estado de guardado (deshabilita los botones). */
  @Input() loading = false;

  @Output() onHide = new EventEmitter<void>();
  /** Emite el payload completo que el padre persiste vía PUT. */
  @Output() onSave = new EventEmitter<{ folio: string; oldId: string }>();

  readonly maxLen = FOLIO_DIGIT_MAX_LENGTH;
  parsed: ParsedFolio | null = null;
  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      digit: ['', [Validators.required, Validators.pattern(/^\d{1,3}$/)]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.parsed = parseFolio(this.folio);
      // Modo edit precarga el dígito actual; modo insert arranca vacío.
      this.form.reset({ digit: this.parsed?.mode === 'edit' ? this.parsed.currentDigit : '' });
    }
  }

  get modeLabel(): string {
    return this.parsed?.mode === 'insert'
      ? 'Agregar primer número del folio (folio legacy sin oldId)'
      : 'Editar primer número del folio';
  }

  /** Sanitiza la entrada: solo dígitos, máximo 3. */
  onDigitInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value ?? '';
    const clean = raw.replace(/\D/g, '').slice(0, this.maxLen);
    this.form.patchValue({ digit: clean });
  }

  get previewFolio(): string | null {
    const d = (this.form.value.digit ?? '').toString();
    return this.parsed && isFolioDigitValid(d) ? buildFolio(this.parsed, d) : null;
  }

  /** Habilita Guardar solo si el folio es parseable, el dígito es válido y cambió. */
  get canSave(): boolean {
    if (!this.parsed || this.form.invalid) return false;
    const d = (this.form.value.digit ?? '').toString();
    const changed = this.parsed.mode === 'insert' ? d.length > 0 : d !== this.parsed.currentDigit;
    return changed;
  }

  /**
   * Cierra cuando el usuario usa la X o la tecla Escape.
   *
   * PrimeNG no cierra el diálogo por su cuenta: `close()` solo emite `visibleChange` y espera que el
   * padre baje la bandera. Con `[visible]` de una sola vía nadie lo escuchaba, así que la X no hacía
   * nada y `onHide` —que se emite al terminar la animación de salida— nunca llegaba. «Cancelar» sí
   * cerraba porque llama a `hide()` directo. Aquí se traduce a esa misma salida.
   */
  onVisibleChange(open: boolean): void {
    if (!open) this.hide();
  }

  hide(): void {
    this.onHide.emit();
  }

  save(): void {
    if (!this.canSave || !this.parsed || !this.folio) {
      this.form.markAllAsTouched();
      return;
    }
    const digit = (this.form.value.digit ?? '').toString();
    // Regla del owner: `oldId` = EXCLUSIVAMENTE el número (el dígito), NO el folio anterior.
    //   oldId = dígito (el número 1–999, queda en sync con el 1er grupo del folio)
    //   folio = folio reconstruido con el nuevo dígito (`R-{oldId}-…`)
    this.onSave.emit({ folio: buildFolio(this.parsed, digit), oldId: digit });
  }
}
